import * as React from 'react';
import { useLocation } from 'react-router-dom';

import { useErrorReporter } from '@mdlinx/frontend-shared';
import { failed, Result, Auth } from '@mdlinx/shared';
import {
  PasswordAssistanceForm,
  PasswordAssistanceFormProps,
} from 'app/components/login-forms/password-assistance-form';

import { useSendPasswordResetEmail, useSendLoginLinkEmail } from 'lib/effect-hooks';
import { trackEvent, getLocationDetails } from 'lib/analytics';
import { FormContainer } from 'app/components/layout/form-container';

function sendMessage(message: Auth.EmailEvent) {
  if (window.parent) {
    window.parent.postMessage(message, '*');
  }
}

export const PasswordHelpForm = ({
  onClickBack = () => {},
  onSuccess,
}: {
  onClickBack?: () => void;
  onSuccess: (actionType: string) => void;
}) => {
  const sendPasswordResetEmail = useSendPasswordResetEmail();
  const sendLoginLinkEmail = useSendLoginLinkEmail();
  const errorReporter = useErrorReporter();
  const [result, setResult] = React.useState<Result<string, string> | undefined>();
  const location = useLocation();

  const onSubmit: PasswordAssistanceFormProps['onSubmit'] = ({ email, actionType }, actions) => {
    setResult(undefined);
    trackEvent('auth_click', { action: 'onSubmit', ...getLocationDetails(location.pathname, location.search) });
    switch (actionType) {
      case 'password-reset': {
        return sendPasswordResetEmail(email)
          .then(
            res => {
              return res.fold(
                () => {
                  onSuccess(actionType);
                  trackEvent('auth_password_reset_succeeded', {
                    action: 'onSubmit',
                    ...getLocationDetails(location.pathname, location.search),
                  });
                },
                e => {
                  switch (e) {
                    // TODO: Should we handle this?
                    case 'USER_NOT_FOUND':
                      return setResult(failed('User not found.'));
                  }
                },
              );
            },
            e => {
              errorReporter.report(e);
              setResult(failed('Oops! We were unable to send a password reset email!'));
              trackEvent('auth_password_reset_failed', {
                action: 'onSubmit',
                ...getLocationDetails(location.pathname, location.search),
              });
            },
          )
          .finally(() => actions.setSubmitting(false));
      }
      case 'login-link': {
        return sendLoginLinkEmail(email)
          .then(() => {
            sendMessage(Auth.loginLinkSent);
            onSuccess(actionType);
          })
          .catch((error: any) => {
            setResult(failed('Failed to send email.'));
            console.error(error);
          });
      }
      default: {
        console.log('BAD ACTION TYPE');
        return;
      }
    }
  };

  const errorMessage =
    result &&
    result.fold(
      message => message,
      message => message,
    );

  return (
    <FormContainer>
      <PasswordAssistanceForm
        onSubmit={onSubmit}
        onClickBack={() => {
          trackEvent('auth_click', {
            action: 'onClickBack',
            ...getLocationDetails(location.pathname, location.search),
          });
          onClickBack();
        }}
        errorMessage={errorMessage}
      />
    </FormContainer>
  );
};
